import type { AIUsageWindow } from '@/types/control/ai-usage';
import type { FieldMappingFilter } from '@/types/control/field-mapping';

/**
 * Query keys for the cross-tenant admin area. These sit outside
 * `hooks/scoped/**` because nothing here is keyed by the *current* tenant: the
 * admin area does not live under a tenant slug, and a tenant id here is a
 * subject the staff user picked, not their own tenancy.
 */
export const adminQueryKeys = {
  staffIdentity: () => ['control', 'staff-identity'] as const,

  /**
   * One key for the tenant list, shared by every page that needs it. Field
   * Mappings, AI Usage, Mesh Outbox and Feature Flags all want the same tenant
   * picker, and Tenants and Users wants the same listing.
   */
  tenants: () => ['control', 'admin-tenants'] as const,

  fieldMappings: (tenantId?: string, filter?: FieldMappingFilter) =>
    [
      'control',
      'field-mappings',
      tenantId ?? null,
      filter?.sourceField ?? null,
      filter?.normalizedField ?? null,
      filter?.collectorType ?? null,
      filter?.isActive ?? null,
    ] as const,

  fieldMappingsByTenant: (tenantId?: string) =>
    ['control', 'field-mappings', tenantId ?? null] as const,

  /**
   * The window is part of the key, so changing a date refetches rather than
   * filtering a cached roll-up on the client. It has to: the summary carries no
   * time buckets, so only the server can answer for a window.
   */
  aiUsage: (window?: AIUsageWindow) =>
    [
      'control',
      'ai-usage',
      window?.startDate ?? null,
      window?.endDate ?? null,
    ] as const,
  meshOutboxGlobal: () => ['control', 'mesh-outbox', 'global'] as const,
  meshOutboxTenants: () => ['control', 'mesh-outbox', 'tenants'] as const,
  meshConnectivity: () => ['control', 'mesh-connectivity'] as const,
  tenantMeshStatus: () => ['control', 'tenant-mesh-status'] as const,
  tenantMeshUserSync: (tenantId?: string) =>
    ['control', 'tenant-mesh-user-sync', tenantId ?? null] as const,
  tenantSyncStatus: () => ['control', 'tenant-sync-status'] as const,
  tenantUsers: (tenantId?: string) =>
    ['control', 'tenant-users', tenantId ?? null] as const,

  /** One tenant's auth settings (HYD-2113); a correction invalidates this. */
  tenantAuth: (tenantId: string) =>
    ['control', 'tenant-auth', tenantId] as const,
  /** Both staff groups in one read; grant and revoke invalidate this key. */
  staffMembers: () => ['control', 'staff-members'] as const,
  /**
   * Deliberately not tenant-keyed: `tenant_id` is not a parameter of the flags
   * route, so one cached read serves every tenant selection and the picker only
   * changes which column of `enabled_tenant_ids` the rows render.
   */
  featureFlags: () => ['control', 'feature-flags'] as const,
};

export const adminMutationKeys = {
  saveFieldMapping: (tenantId?: string) =>
    ['saveFieldMapping', tenantId ?? null] as const,
  deleteFieldMapping: (tenantId?: string) =>
    ['deleteFieldMapping', tenantId ?? null] as const,
  seedFieldMappings: (tenantId?: string) =>
    ['seedFieldMappings', tenantId ?? null] as const,
  backfillMeshUsers: (tenantId?: string) =>
    ['backfillMeshUsers', tenantId ?? null] as const,
  addTenantUser: (tenantId?: string) =>
    ['addTenantUser', tenantId ?? null] as const,
  triggerTenantSync: (tenantId?: string) =>
    ['triggerTenantSync', tenantId ?? null] as const,
  migrateTenantShards: () => ['migrateTenantShards'] as const,
  deleteTenant: () => ['deleteTenant'] as const,
  provisionTenant: () => ['provisionTenant'] as const,
  updateTenantAuth: (tenantId: string) =>
    ['updateTenantAuth', tenantId] as const,
  addTenantAdmin: (tenantId: string) => ['addTenantAdmin', tenantId] as const,
  grantTenantCreator: () => ['grantTenantCreator'] as const,
  revokeTenantCreator: () => ['revokeTenantCreator'] as const,
  setTenantFeatureFlag: (tenantId?: string) =>
    ['setTenantFeatureFlag', tenantId ?? null] as const,
};
